import Animated from 'react-native-reanimated';
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { format, parse } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { AppView, Button, ButtonGroup, SettingsSection, SettingsItemNavigate, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import { IconBoldCalendar, IconProfile } from 'design-system/icons';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useDateTimePickerDOB, { DOB_DISPLAY_FORMAT, DOB_STORAGE_FORMAT, } from 'hooks/useDateTimePickerDOB';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { registerUser } from 'actions';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { TitleModal } from 'features/borrow/components/monevo/TitleModal';
import { TITLES } from 'features/borrow/monevo/models';
import { useAppSelector } from 'store/hooks';
import { selectUser } from 'reducers/selectors';
import { Title } from 'reducers/types';
import { isValidCreditScoreAge } from '../utils';
import useCreditScoreNavigation from '../hooks/useCreditScoreNavigation';
import CreditScoreInvalidAgeModal from '../components/CreditScoreInvalidAgeModal';
const strings = {
    title: 'Personal details',
    titleRow: 'Title',
    firstName: 'Legal first name',
    middleName: 'Legal middle name (optional)',
    lastName: 'Legal last name',
    dob: 'Date of birth',
    defaultTitle: 'Choose...',
    buttonNext: 'Continue',
    subTitle: 'Please make sure your details are accurate and up to date',
};
const CreditScorePersonalDetailsScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const user = useAppSelector(selectUser);
    const { navigateToNextStep } = useCreditScoreNavigation();
    const areFieldsDisabled = Boolean(user.kycStartedAt || user.creditScoreIdentityLockedAt);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    // An existing title of 'Other' is not prefilled — the user has to make
    // an explicit choice here.
    const [title, setTitle] = useState(() => user?.title === Title.Other ? '' : user?.title ?? '');
    const [firstName, setFirstName] = useState(user?.firstName ?? '');
    const [middleName, setMiddleName] = useState(user?.middleName ?? '');
    const [lastName, setLastName] = useState(user?.lastName ?? '');
    const { showDateTimePicker, dob } = useDateTimePickerDOB(user, 0);
    const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
    const titleLabel = TITLES.find((t) => t.value === title)?.label || strings.defaultTitle;
    const openTitle = useCallback(() => {
        setIsTitleModalOpen(true);
        TitleModal.show((titleObj) => {
            setTitle(titleObj.value);
            setIsTitleModalOpen(false);
        }, undefined, () => {
            setIsTitleModalOpen(false);
        });
    }, []);
    const changeName = useCallback(() => {
        navigation.navigate('ChangeDetail', {
            key: 'firstName',
            defaultValue: firstName,
            onSuccess: setFirstName,
        });
    }, [firstName]);
    const changeMiddleName = useCallback(() => {
        navigation.navigate('ChangeDetail', {
            key: 'middleName',
            defaultValue: middleName,
            onSuccess: setMiddleName,
        });
    }, [middleName]);
    const changeLastName = useCallback(() => {
        navigation.navigate('ChangeDetail', {
            key: 'lastName',
            defaultValue: lastName,
            onSuccess: setLastName,
        });
    }, [lastName]);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const isFormComplete = useMemo(() => Boolean(title &&
        firstName.trim().length > 0 &&
        lastName.trim().length > 0 &&
        dob), [title, firstName, lastName, dob]);
    const onPressContinue = useCallback(async () => {
        if (!isValidCreditScoreAge(dob)) {
            CreditScoreInvalidAgeModal.show();
            return;
        }
        const data = {};
        if (title !== user.title) {
            data.title = title;
        }
        if (firstName.trim().length > 0 &&
            firstName.trim() !== user.firstName) {
            data.firstName = firstName.trim();
        }
        if (middleName.trim().length > 0 &&
            middleName.trim() !== user.middleName) {
            data.middleName = middleName.trim();
        }
        if (lastName.trim().length > 0 && lastName.trim() !== user.lastName) {
            data.lastName = lastName.trim();
        }
        if (!user.dateOfBirth ||
            dob !==
                format(toZonedTime(user.dateOfBirth, 'UTC'), DOB_STORAGE_FORMAT)) {
            data.dateOfBirth = dob;
        }
        if (Object.keys(data).length) {
            const res = await fetch(registerUser(data));
            if (res) {
                navigateToNextStep();
            }
        }
        else {
            navigateToNextStep();
        }
    }, [
        title,
        dob,
        firstName,
        lastName,
        middleName,
        user.title,
        user.dateOfBirth,
        user.firstName,
        user.lastName,
        user.middleName,
        navigateToNextStep,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(false);
    const scrollViewStyle = useMarginBottom('paddingBottom', HEIGHT, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView onScroll={scrollHandler} contentContainerStyle={scrollViewStyle}>
            <ScreenInfoHeader title={strings.title} subTitle={strings.subTitle}/>
            <SettingsSection noMarginTop>
              <SettingsItemNavigate icon={<IconProfile gradientKey="time"/>} description={strings.titleRow} title={titleLabel} onPress={openTitle} isSelected={isTitleModalOpen}/>
              <SettingsItemNavigate icon={<IconProfile gradientKey="darkPurple"/>} disabled={areFieldsDisabled} description={strings.firstName} title={firstName || strings.defaultTitle} onPress={changeName}/>
              <SettingsItemNavigate icon={<IconProfile gradientKey="lightPink"/>} disabled={areFieldsDisabled} description={strings.middleName} title={middleName || strings.defaultTitle} onPress={changeMiddleName}/>
              <SettingsItemNavigate icon={<IconProfile gradientKey="skyBlue"/>} disabled={areFieldsDisabled} description={strings.lastName} title={lastName || strings.defaultTitle} onPress={changeLastName}/>
              <SettingsItemNavigate icon={<IconBoldCalendar gradientKey="darkPurple"/>} disabled={areFieldsDisabled ? Boolean(dob) : undefined} title={dob
            ? format(parse(dob, DOB_STORAGE_FORMAT, new Date()), DOB_DISPLAY_FORMAT)
            : strings.defaultTitle} description={strings.dob} onPress={showDateTimePicker} isSelected={false}/>
            </SettingsSection>
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button brand disabled={!isFormComplete} onPress={onPressContinue} title={strings.buttonNext} isFetching={isFetching}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
    headerTintColor: colors.text.primary,
}));
export default CreditScorePersonalDetailsScreen;
